import {
  Ban,
  Clock3,
  Coins,
  Database,
  LockKeyhole,
  Menu,
  MessageSquareText,
  Radio,
  Send,
  ShieldCheck,
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Button,
  Drawer,
  InlineNotice,
  MetadataStrip,
  MotionSelector,
  Panel,
  StatusPill,
  Workbench,
} from "../../design/primitives";
import {
  ACTIVE_RUN_STATES,
  MAX_RUN_MESSAGE_UTF8_BYTES,
  PortalGatewayError,
  TERMINAL_RUN_STATES,
  isValidRunMessage,
  type PortalGateway,
  type RunState,
} from "./api";
import {
  ConversationHistoryView,
  ConversationRail,
} from "./ConversationHistory";
import {
  ExecutionRail,
  type ExecutionStep,
} from "./ExecutionRail";
import {
  executionStepsFor,
  formatServerDateTime,
  runStateLabel,
  shortResourceId,
  type ConnectionState,
  type RunUiState,
} from "./model";
import { useConversationHistory } from "./useConversationHistory";
import { useM1Run } from "./useM1Run";

type M1ShellProps = {
  gateway: PortalGateway | null;
  runId: string | null;
};

const stateTone = (
  state: RunState,
): "info" | "success" | "warning" | "error" => {
  if (state === "SUCCEEDED") {
    return "success";
  }
  if (state === "FAILED" || state === "REVOKED" || state === "EXPIRED") {
    return "error";
  }
  if (state === "CANCELLED") {
    return "warning";
  }
  return "info";
};

const connectionPresentation: Record<
  ConnectionState,
  {
    label: string;
    tone: "neutral" | "info" | "success" | "warning" | "error";
  }
> = {
  unavailable: { label: "API neconectat", tone: "warning" },
  "no-run": { label: "Nicio rulare", tone: "neutral" },
  loading: { label: "Verificare API", tone: "info" },
  connected: { label: "API verificat", tone: "success" },
  reconnecting: { label: "Reconectare", tone: "warning" },
  interrupted: { label: "Replay întrerupt", tone: "warning" },
  error: { label: "Date indisponibile", tone: "error" },
};

const errorCopy: Record<string, string> = {
  RUN_NOT_FOUND:
    "Rularea nu este disponibilă pentru această sesiune și acest grant.",
  STREAM_EXPIRED:
    "Istoricul criptat a expirat. Starea finală rămâne disponibilă dacă sesiunea încă o autorizează.",
  STREAM_BACKLOG_EXCEEDED:
    "Istoricul este mai mare decât fereastra acceptată de browser. Reîncărcarea parțială a fost refuzată.",
  STREAM_UNAVAILABLE:
    "Fluxul nu mai poate fi continuat în siguranță. Starea durabilă a fost păstrată.",
  STREAM_WINDOW_CLOSED:
    "Fereastra live s-a închis. Reîncarcă pagina pentru o nouă verificare autorizată.",
  STREAM_SEQUENCE_GAP:
    "Fluxul a avut o discontinuitate și a fost oprit înainte de a afișa un rezultat ambiguu.",
  STREAM_CURSOR_INVALID:
    "Cursorul de reluare nu mai descrie istoricul acestei rulări. Starea durabilă a fost recitită.",
  PORTAL_NETWORK_ERROR:
    "Conexiunea cu portalul nu a putut fi refăcută în bugetul permis.",
  SERVICE_UNAVAILABLE:
    "Sesiunea BFF sau serviciul necesar nu este disponibil.",
  CSRF_DENIED:
    "Sesiunea browser nu mai poate autoriza acțiuni. Reautentificarea este necesară.",
  INVALID_REQUEST:
    "Mesajul trebuie să conțină text valid și să respecte limita de 3.072 de octeți.",
  SESSION_UNAVAILABLE:
    "Sesiunea nu mai poate porni o rulare. Reîncarcă pagina după reautentificare.",
  ACTIVE_RUN_EXISTS:
    "Sesiunea are deja o rulare activă. Cererea nouă nu a fost pornită.",
};

const ControlItem = ({
  icon,
  title,
  detail,
}: {
  icon: ReactNode;
  title: string;
  detail: string;
}) => (
  <li className="control-item">
    <span className="control-item__icon" aria-hidden="true">
      {icon}
    </span>
    <div>
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  </li>
);

const EmptyWorkbench = ({
  icon,
  title,
  children,
  busy = false,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  busy?: boolean;
}) => (
  <div className="workbench__empty" aria-busy={busy || undefined}>
    <span className="workbench__empty-icon" aria-hidden="true">
      {icon}
    </span>
    <div>
      <h3>{title}</h3>
      <p>{children}</p>
    </div>
  </div>
);

const StreamViewport = ({ state }: { state: RunUiState }) => {
  if (state.connection === "unavailable") {
    return (
      <EmptyWorkbench
        icon={<LockKeyhole />}
        title="Bootstrapul organizațional lipsește"
      >
        Buildul nu simulează identitate, sesiune sau provider. BFF-ul real
        trebuie să autorizeze pagina înainte ca browserul să citească o rulare.
      </EmptyWorkbench>
    );
  }
  if (state.connection === "no-run") {
    return (
      <EmptyWorkbench
        icon={<MessageSquareText />}
        title="Pregătită pentru cerere"
      >
        Scrie un singur mesaj pentru a porni rularea autorizată. Lista de
        sesiuni și conversațiile multi-turn nu fac parte din acest contract.
      </EmptyWorkbench>
    );
  }
  if (state.connection === "loading") {
    return (
      <EmptyWorkbench
        icon={<Radio />}
        title="Verificăm rularea"
        busy
      >
        Portalul confirmă ownershipul, sesiunea și grantul înainte de a deschide
        fluxul criptat.
      </EmptyWorkbench>
    );
  }
  if (!state.run) {
    return (
      <EmptyWorkbench
        icon={<Ban />}
        title="Rularea nu poate fi afișată"
      >
        {errorCopy[state.errorCode ?? ""] ??
          "Datele nu au trecut verificarea de contract."}
      </EmptyWorkbench>
    );
  }

  return (
    <div className="stream-scene">
      {state.chunks.length > 0 ? (
        <section
          className="stream-viewport"
          aria-labelledby="stream-title"
          aria-busy={
            ACTIVE_RUN_STATES.has(state.run.state) || undefined
          }
        >
          <div className="stream-viewport__heading">
            <div>
              <p className="eyebrow eyebrow--dark">Rezultat autorizat</p>
              <h3 id="stream-title">Răspunsul rulării</h3>
            </div>
            <span className="is-mono">
              {state.chunks.length}{" "}
              {state.chunks.length === 1 ? "fragment" : "fragmente"}
            </span>
          </div>
          <div className="stream-viewport__content">
            {state.chunks.map((chunk) => (
              <p key={chunk.sequence} data-sequence={chunk.sequence}>
                {chunk.text}
              </p>
            ))}
          </div>
        </section>
      ) : (
        <EmptyWorkbench
          icon={<Radio />}
          title={
            TERMINAL_RUN_STATES.has(state.run.state)
              ? "Rularea nu are rezultat disponibil"
              : "Așteptăm primul fragment autorizat"
          }
          busy={ACTIVE_RUN_STATES.has(state.run.state)}
        >
          {TERMINAL_RUN_STATES.has(state.run.state)
            ? "Starea terminală este păstrată, dar proiecția curentă nu conține text de rezultat."
            : "Fluxul rămâne reluabil; heartbeatul nu este prezentat ca activitate a providerului."}
        </EmptyWorkbench>
      )}

      {TERMINAL_RUN_STATES.has(state.run.state) ? (
        <div
          className={`persistent-outcome persistent-outcome--${stateTone(
            state.run.state,
          )}`}
          role="status"
        >
          <strong>{runStateLabel(state.run.state)}</strong>
          <span>
            {state.run.result_state === "PARTIAL"
              ? "Rezultatul afișat este parțial."
              : state.run.result_state === "COMPLETE"
                ? "Rezultatul este complet."
                : "Nu există rezultat disponibil."}
          </span>
        </div>
      ) : null}

      {state.connection === "reconnecting" ? (
        <div className="stream-state" role="status">
          <Radio size={16} aria-hidden="true" />
          <span>Reconectăm de la secvența {state.cursor}.</span>
        </div>
      ) : null}
      {state.connection === "interrupted" ||
      state.connection === "error" ? (
        <div className="stream-state stream-state--error" role="alert">
          <Ban size={16} aria-hidden="true" />
          <span>
            {errorCopy[state.errorCode ?? ""] ??
              "Fluxul a fost oprit înainte de a afișa date nevalidate."}
          </span>
        </div>
      ) : null}
    </div>
  );
};

const RevocationControl = ({
  state,
  onConfirm,
  onDismiss,
  onRevoke,
}: {
  state: RunUiState;
  onConfirm: () => void;
  onDismiss: () => void;
  onRevoke: () => Promise<void>;
}) => {
  const canRevoke =
    state.run !== null && ACTIVE_RUN_STATES.has(state.run.state);

  if (state.revoke === "confirming") {
    return (
      <div className="revocation-confirm" role="alert">
        <strong>Revoci accesul la această rulare?</strong>
        <p>
          Lease-ul activ va fi invalidat. Costul deja produs rămâne reconciliat.
        </p>
        <div className="revocation-confirm__actions">
          <Button variant="ghost" onClick={onDismiss}>
            Păstrează rularea
          </Button>
          <Button variant="destructive" onClick={() => void onRevoke()}>
            Revocă rularea
          </Button>
        </div>
      </div>
    );
  }
  if (state.revoke === "succeeded" || state.run?.state === "REVOKED") {
    return (
      <div className="revocation-outcome" role="status">
        <Ban size={18} aria-hidden="true" />
        <div>
          <strong>Acces revocat</strong>
          <span>Fluxul a fost oprit și rezultatul rămas este etichetat.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="revocation-control">
      <Button
        variant="destructive"
        disabled={!canRevoke}
        busy={state.revoke === "applying"}
        onClick={onConfirm}
      >
        <Ban size={17} aria-hidden="true" />
        {state.revoke === "applying"
          ? "Aplicăm revocarea"
          : "Pregătește revocarea"}
      </Button>
      <p>
        {canRevoke
          ? "Confirmarea este obligatorie; acțiunea rămâne auditabilă."
          : "Revocarea este disponibilă numai cât rularea este activă."}
      </p>
      {state.revoke === "failed" ? (
        <span className="revocation-control__error" role="alert">
          Revocarea nu a fost confirmată de API.
        </span>
      ) : null}
    </div>
  );
};

export const M1Shell = ({ gateway, runId }: M1ShellProps) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState(runId);
  const [sessionResolution, setSessionResolution] = useState<
    "pending" | "resolved" | "failed"
  >(gateway && !runId ? "pending" : "resolved");
  const [message, setMessage] = useState("");
  const [submission, setSubmission] = useState<
    "idle" | "submitting" | "failed"
  >("idle");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const submissionController = useRef<AbortController | null>(null);
  const refreshedTerminalRun = useRef<string | null>(null);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const adoptRun = useCallback((identifier: string) => {
    setSelectedRunId(identifier);
    const url = new URL(window.location.href);
    url.searchParams.set("run", identifier);
    window.history.replaceState(window.history.state, "", url);
  }, []);
  const {
    state,
    confirmRevoke,
    dismissRevoke,
    revoke,
  } = useM1Run(gateway, selectedRunId);
  const history = useConversationHistory(gateway);
  useEffect(() => {
    setSelectedRunId(runId);
    if (!gateway || runId) {
      setSessionResolution("resolved");
      return undefined;
    }
    const controller = new AbortController();
    setSessionResolution("pending");
    setSubmissionError(null);
    const discover = async () => {
      try {
        const active = await gateway.readActiveRun(controller.signal);
        if (active) {
          adoptRun(active.run_id);
        }
        setSessionResolution("resolved");
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const failure =
          error instanceof PortalGatewayError
            ? error
            : new PortalGatewayError({ code: "PORTAL_REQUEST_FAILED" });
        setSubmissionError(failure.code);
        setSessionResolution("failed");
      }
    };
    void discover();
    return () => controller.abort();
  }, [adoptRun, gateway, runId]);
  useEffect(
    () => () => {
      submissionController.current?.abort();
    },
    [],
  );
  useEffect(() => {
    if (
      state.run &&
      TERMINAL_RUN_STATES.has(state.run.state) &&
      refreshedTerminalRun.current !== state.run.run_id
    ) {
      refreshedTerminalRun.current = state.run.run_id;
      void history.refreshConversations();
    }
  }, [history.refreshConversations, state.run]);
  useEffect(() => {
    if (history.resumeState === "ready") {
      messageInputRef.current?.focus({ preventScroll: true });
    }
  }, [history.resumeState]);
  const connection = connectionPresentation[state.connection];
  const executionSteps: ExecutionStep[] = useMemo(
    () => executionStepsFor(state.run),
    [state.run],
  );
  const railStatus = state.run
    ? runStateLabel(state.run.state)
    : "Nicio rulare activă";
  const railTone = state.run ? stateTone(state.run.state) : "neutral";
  const canCompose =
    gateway !== null &&
    selectedRunId === null &&
    sessionResolution === "resolved" &&
    state.connection === "no-run";
  const canPrepareResume = canCompose;
  const validMessage = isValidRunMessage(message);

  const submit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (
        !gateway ||
        !canCompose ||
        !validMessage ||
        submissionController.current !== null
      ) {
        return;
      }
      const controller = new AbortController();
      submissionController.current = controller;
      setSubmission("submitting");
      setSubmissionError(null);
      try {
        const command = await gateway.submitRun(
          message,
          controller.signal,
          history.resume?.resume_token,
        );
        setMessage("");
        history.closeConversation();
        adoptRun(command.run_id);
        setSubmission("idle");
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        const failure =
          error instanceof PortalGatewayError
            ? error
            : new PortalGatewayError({ code: "PORTAL_REQUEST_FAILED" });
        try {
          const active = await gateway.readActiveRun(controller.signal);
          if (active) {
            setMessage("");
            history.closeConversation();
            adoptRun(active.run_id);
            setSubmission("idle");
            return;
          }
        } catch {
          // The original closed error remains the only user-facing failure.
        }
        setSubmissionError(failure.code);
        if (failure.code === "RESUME_INTENT_UNAVAILABLE") {
          history.clearResume(failure.code);
        }
        setSubmission("failed");
        window.setTimeout(() => messageInputRef.current?.focus(), 0);
      } finally {
        if (submissionController.current === controller) {
          submissionController.current = null;
        }
      }
    },
    [
      adoptRun,
      canCompose,
      gateway,
      history,
      message,
      validMessage,
    ],
  );

  return (
    <>
      <a className="skip-link" href="#main-content">
        Sari la conținut
      </a>
      <div className="app-shell">
        <aside className="sidebar" aria-label="Navigare sesiuni">
          <div className="brand-lockup brand-lockup--sidebar">
            <span className="brand-mark" aria-hidden="true">
              G
            </span>
            <span>
              <strong>GESEIDL</strong>
              <small>Portal AI</small>
            </span>
          </div>
          <ConversationRail
            history={history}
            run={state.run}
          />
        </aside>

        <div className="app-main">
          <header className="topbar">
            <div className="topbar__identity">
              <Button
                ref={menuButtonRef}
                variant="ghost"
                className="button--icon topbar__menu"
                aria-label="Deschide sesiunile"
                aria-expanded={drawerOpen}
                onClick={() => setDrawerOpen(true)}
              >
                <Menu size={20} aria-hidden="true" />
              </Button>
              <div className="brand-lockup brand-lockup--compact">
                <span className="brand-mark" aria-hidden="true">
                  G
                </span>
                <strong>Portal AI</strong>
              </div>
              <div className="topbar__context">
                <span>Vertical slice M1</span>
                <strong>
                  {state.run ? "Rulare autorizată" : "Control plane"}
                </strong>
              </div>
            </div>
            <div className="topbar__controls">
              <MotionSelector />
              <StatusPill tone={connection.tone}>
                {connection.label}
              </StatusPill>
            </div>
          </header>

          <main id="main-content" className="page" tabIndex={-1}>
            <header className="page-heading">
              <div>
                <p className="eyebrow">Sesiune organizațională</p>
                <h1>Urmărește rularea organizațională</h1>
                <p>
                  Flux autorizat, stare durabilă și revocare într-o singură
                  scenă.
                </p>
              </div>
              <div className="page-heading__status">
                <ShieldCheck size={20} aria-hidden="true" />
                <div>
                  <span>Acces controlat</span>
                  <strong>
                    {state.run
                      ? "Ownership și grant confirmate de API"
                      : "Identitatea nu este dedusă din browser"}
                  </strong>
                </div>
              </div>
            </header>

            {state.connection === "unavailable" ? (
              <InlineNotice title="Integrare în așteptare" tone="info">
                IdP-ul, bootstrapul BFF, providerul și secret store-ul sunt
                gate-uri externe. Buildul de producție nu conține dev-login
                sau date simulate.
              </InlineNotice>
            ) : state.connection === "no-run" ? (
              <InlineNotice
                title={
                  history.resumeState === "ready"
                    ? "Reluare pregătită"
                    : "Cerere nouă disponibilă"
                }
                tone="info"
              >
                {history.resumeState === "ready"
                  ? "Mesajul următor va cere din nou autorizarea și politica de egress pentru istoricul selectat."
                  : "Mesajul este trimis numai prin sesiunea BFF autorizată și este acceptat o singură dată pentru această rulare."}
              </InlineNotice>
            ) : null}

            <ConversationHistoryView
              history={history}
              canResume={canPrepareResume}
            />

            <div className="scene-grid">
              <Workbench aria-labelledby="workbench-title">
                <header className="workbench__header">
                  <div>
                    <p className="eyebrow eyebrow--dark">
                      {state.run
                        ? shortResourceId(state.run.run_id)
                        : "Rulare"}
                    </p>
                    <h2 id="workbench-title">Spațiu de lucru</h2>
                  </div>
                  <StatusPill
                    tone={state.run ? stateTone(state.run.state) : "neutral"}
                    pulse={
                      state.run
                        ? ACTIVE_RUN_STATES.has(state.run.state)
                        : false
                    }
                  >
                    {state.run
                      ? runStateLabel(state.run.state)
                      : "În așteptarea autorizării"}
                  </StatusPill>
                </header>

                <ExecutionRail
                  steps={executionSteps}
                  status={railStatus}
                  statusTone={railTone}
                />

                <StreamViewport state={state} />

                <form
                  className={`composer${canCompose ? "" : " composer--readonly"}`}
                  onSubmit={(event) => void submit(event)}
                >
                  <label htmlFor="message">Mesaj nou</label>
                  <div className="composer__field">
                    <textarea
                      ref={messageInputRef}
                      id="message"
                      rows={2}
                      value={message}
                      onChange={(event) => {
                        setMessage(event.target.value);
                        if (submission === "failed") {
                          setSubmission("idle");
                          setSubmissionError(null);
                        }
                      }}
                      disabled={!canCompose || submission === "submitting"}
                      maxLength={MAX_RUN_MESSAGE_UTF8_BYTES}
                      placeholder={
                        canCompose
                          ? history.resumeState === "ready"
                            ? "Scrie mesajul care continuă conversația"
                            : "Scrie cererea organizațională"
                          : sessionResolution === "pending"
                            ? "Verificăm dacă sesiunea are o rulare activă"
                          : "Disponibil pentru o sesiune autorizată fără rulare"
                      }
                      aria-describedby={
                        submissionError
                          ? "message-help message-error"
                          : "message-help"
                      }
                    />
                    <Button
                      type="submit"
                      className="button--send"
                      busy={submission === "submitting"}
                      disabled={!canCompose || !validMessage}
                    >
                      <Send size={17} aria-hidden="true" />
                      <span className="composer__send-label">
                        {submission === "submitting"
                          ? "Trimitem în siguranță"
                          : "Pornește rularea"}
                      </span>
                    </Button>
                  </div>
                  <div className="composer__meta" id="message-help">
                    <span>
                      Maximum 3.072 de octeți UTF-8
                      {history.resumeState === "ready"
                        ? " · reluare pregătită"
                        : ""}.
                    </span>
                    <span className="is-mono">
                      secvență {state.cursor || "—"} · cost neexpus
                    </span>
                  </div>
                  {submissionError ? (
                    <p className="composer__error" id="message-error" role="alert">
                      {errorCopy[submissionError] ??
                        "Cererea nu a fost confirmată de API. Mesajul a fost păstrat pentru reîncercare."}
                    </p>
                  ) : null}
                </form>
              </Workbench>

              <aside className="scene-inspector" aria-label="Controale M1">
                <Panel variant="raised">
                  <div className="panel__header">
                    <div>
                      <p className="eyebrow">Rulare selectată</p>
                      <h2>Stare și control</h2>
                    </div>
                    <StatusPill
                      tone={state.run ? stateTone(state.run.state) : "neutral"}
                    >
                      {state.run ? runStateLabel(state.run.state) : "Fără date"}
                    </StatusPill>
                  </div>
                  {state.run ? (
                    <>
                      <MetadataStrip
                        items={[
                          {
                            label: "Rulare",
                            value: shortResourceId(state.run.run_id),
                            mono: true,
                          },
                          {
                            label: "Deadline",
                            value: formatServerDateTime(
                              state.run.deadline_at,
                            ),
                          },
                          {
                            label: "Usage",
                            value:
                              state.run.usage_state === "RECONCILED"
                                ? "Reconciliat"
                                : state.run.usage_state ===
                                    "PENDING_RECONCILIATION"
                                  ? "În reconciliere"
                                  : "Rezervat",
                          },
                          {
                            label: "Rezultat",
                            value:
                              state.run.result_state === "COMPLETE"
                                ? "Complet"
                                : state.run.result_state === "PARTIAL"
                                  ? "Parțial"
                                  : "Indisponibil",
                          },
                          {
                            label: "Cost exact",
                            value: "Neexpus",
                          },
                        ]}
                      />
                      <RevocationControl
                        state={state}
                        onConfirm={confirmRevoke}
                        onDismiss={dismissRevoke}
                        onRevoke={revoke}
                      />
                    </>
                  ) : (
                    <ul className="control-list">
                      <ControlItem
                        icon={<ShieldCheck />}
                        title="Identitate și rol"
                        detail="Verificate server-side"
                      />
                      <ControlItem
                        icon={<Database />}
                        title="Context criptat"
                        detail="TTL obligatoriu"
                      />
                      <ControlItem
                        icon={<Coins />}
                        title="Usage și cost"
                        detail="Fără valori inventate"
                      />
                      <ControlItem
                        icon={<Ban />}
                        title="Revocare"
                        detail="Disponibilă după autorizare"
                      />
                    </ul>
                  )}
                </Panel>

                <Panel variant="soft" className="evidence-panel">
                  <div className="evidence-panel__title">
                    <Clock3 size={18} aria-hidden="true" />
                    <h2>Dovada suprafeței</h2>
                  </div>
                  <MetadataStrip
                    items={[
                      { label: "Sursă", value: "Core Web Design" },
                      { label: "Versiune", value: "1.0.0", mono: true },
                      {
                        label: "Provider/model",
                        value: "Neexpuse de API",
                      },
                      {
                        label: "Flux",
                        value: state.run ? "Owner-bound" : "Închis",
                      },
                    ]}
                  />
                </Panel>
              </aside>
            </div>

            <div className="sr-only" aria-live="polite" aria-atomic="true">
              {state.run
                ? `Starea rulării: ${runStateLabel(state.run.state)}.`
                : connection.label}
            </div>
          </main>
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        title="Sesiuni"
        onClose={closeDrawer}
        returnFocusRef={menuButtonRef}
      >
        <ConversationRail
          history={history}
          run={state.run}
          onSelect={closeDrawer}
        />
      </Drawer>
    </>
  );
};
